// src/hooks/useVoiceChat.js - UPDATED with cross-browser compatibility
import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://holzflex-api-588409326737.us-central1.run.app';
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Helper function for resampling audio (Firefox/Safari compatibility)
function resample(audioData, fromSampleRate, toSampleRate) {
  if (fromSampleRate === toSampleRate) {
    return audioData;
  }
  
  const ratio = fromSampleRate / toSampleRate;
  const newLength = Math.round(audioData.length / ratio);
  const result = new Float32Array(newLength);
  
  // Simple linear interpolation
  for (let i = 0; i < newLength; i++) {
    const position = i * ratio;
    const index = Math.floor(position);
    const fraction = position - index;
    
    if (index + 1 < audioData.length) {
      result[i] = audioData[index] * (1 - fraction) + audioData[index + 1] * fraction;
    } else {
      result[i] = audioData[index];
    }
  }
  
  return result;
}

// Browser detection helper
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  
  if (ua.includes('Firefox/')) {
    return { name: 'Firefox', version: ua.match(/Firefox\/(\d+)/)?.[1], isFirefox: true };
  } else if (ua.includes('Edg/')) {
    return { name: 'Edge', version: ua.match(/Edg\/(\d+)/)?.[1], isEdge: true };
  } else if (ua.includes('OPR/') || ua.includes('Opera/')) {
    return { name: 'Opera', version: ua.match(/(?:OPR|Opera)\/(\d+)/)?.[1], isOpera: true };
  } else if (ua.includes('Chrome/')) {
    return { name: 'Chrome', version: ua.match(/Chrome\/(\d+)/)?.[1], isChrome: true };
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    return { 
      name: isIOS ? 'Mobile Safari' : 'Safari', 
      version: ua.match(/Version\/(\d+)/)?.[1],
      isSafari: true,
      isIOS: isIOS
    };
  }
  
  return { name: 'Unknown', version: null };
};

export const useVoiceChat = (conversationId) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [browserInfo] = useState(() => getBrowserInfo());

  const wsRef = useRef(null);
  
  // Keep stream alive between recordings
  const audioStreamRef = useRef(null);
  const audioStreamInitializedRef = useRef(false);
  const audioContextRef = useRef(null);
  const nativeSampleRateRef = useRef(48000);
  const processorRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const animationFrameRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const speechActiveRef = useRef(false);
  
  // Track if we've sent start_recording to server
  const recordingSessionActiveRef = useRef(false);
  
  // Pre-created audio element
  const audioElementRef = useRef(null);
  const audioInitializedRef = useRef(false);
  const pingIntervalRef = useRef(null);

  /**
   * Initialize audio element with Safari/iOS compatibility
   */
  const initializeAudioElement = useCallback(() => {
    if (audioInitializedRef.current) return;
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    try {
      // console.log('🔊 Initializing audio element...');
      
      const audio = new Audio();
      audio.preload = 'auto';
      audio.autoplay = false;
      audio.setAttribute('playsinline', 'true');
      audio.setAttribute('webkit-playsinline', 'true');
      audio.muted = false; // Safari sometimes needs explicit unmute
      
      audio.onended = () => {
        // console.log('✅ Audio chunk finished');
        playNextAudio();
      };
      
      audio.onerror = (err) => {
        console.error('❌ Audio playback error:', err);
        
        // Safari-specific error handling
        if (err.target?.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
          setError('Audio format not supported on this device');
        } else {
          setError('Audio playback failed');
        }
        
        isPlayingRef.current = false;
        setIsSpeaking(false);
      };
      
      audioElementRef.current = audio;
      audioInitializedRef.current = true;
      
      // console.log('✅ Audio element initialized');
    } catch (err) {
      console.error('Failed to initialize audio element:', err);
    }
  }, []);

  /**
   * Play audio chunks with Safari promise handling
   */
  const playNextAudio = useCallback(async () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      setIsSpeaking(false);
      // console.log('✅ All audio playback complete');
      return;
    }

    if (!audioElementRef.current) {
      console.error('❌ Audio element not initialized');
      setError('Audio playback not ready');
      isPlayingRef.current = false;
      setIsSpeaking(false);
      return;
    }

    isPlayingRef.current = true;
    const blob = audioQueueRef.current.shift();
    
    try {
      const audioBlob = new Blob([blob], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      const audio = audioElementRef.current;
      
      if (audio.src) {
        URL.revokeObjectURL(audio.src);
      }
      
      audio.src = audioUrl;
      audio.load();
      
      // Handle Safari's play() promise rejection
      try {
        await audio.play();
        // console.log('▶️ Playing audio chunk');
      } catch (playErr) {
        // Safari may reject if not enough user interaction
        if (playErr.name === 'NotAllowedError') {
          console.error('❌ Safari blocked audio playback - needs user interaction');
          setError('Tap screen to enable audio playback');
          isPlayingRef.current = false;
          setIsSpeaking(false);
        } else {
          throw playErr;
        }
      }
      
    } catch (err) {
      console.error('Failed to play audio:', err);
      setError('Failed to play audio: ' + err.message);
      isPlayingRef.current = false;
      setIsSpeaking(false);
      audioInitializedRef.current = false;
    }
  }, []);

  /**
   * Initialize microphone with native sample rate (Firefox/Safari fix)
   */
  const initializeMicrophoneStream = useCallback(async () => {
    if (audioStreamInitializedRef.current && audioStreamRef.current) {
      // console.log('🎤 Reusing existing microphone stream');
      return true;
    }

    try {
      // console.log('🎤 Requesting microphone access...');
      // console.log('🌐 Browser:', browserInfo.name, browserInfo.version);

      // Request microphone WITHOUT sample rate constraint (Firefox/Safari fix)
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          // Don't constrain sampleRate - use device native rate
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      audioStreamRef.current = stream;
      audioStreamInitializedRef.current = true;
      
      // Get actual sample rate from the stream
      const track = stream.getAudioTracks()[0];
      const settings = track.getSettings();
      const nativeSampleRate = settings.sampleRate || 48000;
      nativeSampleRateRef.current = nativeSampleRate;
      
      // console.log('✅ Microphone stream initialized');
      // console.log('📊 Native sample rate:', nativeSampleRate);
      
      // Create AudioContext with NATIVE sample rate (not forced 16kHz)
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: nativeSampleRate
      });
      
      // Safari: Resume AudioContext if suspended
      if (audioContext.state === 'suspended') {
        // console.log('🎵 AudioContext suspended (Safari), attempting to resume...');
        await audioContext.resume();
        // console.log('✅ AudioContext resumed, state:', audioContext.state);
      }
      
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      sourceNodeRef.current = source;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      return true;

    } catch (err) {
      console.error('❌ Failed to initialize microphone:', err);
      setError('Could not access microphone: ' + err.message);
      audioStreamInitializedRef.current = false;
      return false;
    }
  }, [browserInfo]);

  /**
   * Handle server messages
   */
  const handleServerMessage = useCallback((message) => {
    // console.log('📨 Server:', message.type, message.message || '');

    switch (message.type) {
      case 'ready':
        // Server is ready — now safe to send authenticate
        // (server's message listener is registered by this point)
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'authenticate',
            conversationId: conversationId
          }));
        }
        break;

      case 'authenticated':
        // console.log('✅ Authenticated — enabling voice input');
        setIsConnected(true);
        break;

      case 'recording_started':
        // console.log('🎙️ Server confirmed recording started');
        recordingSessionActiveRef.current = true;
        break;

      case 'transcript':
        setTranscript(message.text);
        if (message.isFinal) {
          // console.log('📝 Final transcript:', message.text);
        }
        break;

      case 'speech_start':
        // console.log('🔊 Speech generation starting...');
        setIsSpeaking(true);
        setIsProcessing(false);
        break;

      case 'speech_end':
        // console.log('✅ Speech generation complete');
        break;
        
      case 'pong':
        // Server responded to ping - connection is alive
        break;

      case 'error':
        console.error('❌ Server error:', message.message);
        // Clean up audio processing so animation frame loop doesn't keep re-rendering
        if (processorRef.current) {
          processorRef.current.disconnect();
          processorRef.current = null;
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        setError(message.message);
        setIsProcessing(false);
        setIsRecording(false);
        setIsSpeaking(false);
        recordingSessionActiveRef.current = false;
        break;

      default:
        // console.log('Unknown message type:', message.type);
    }
  }, []);

  /**
   * Handle incoming audio data
   */
  const handleAudioData = useCallback((blob) => {
    if (!speechActiveRef.current) {
      // Speech was stopped — drop chunks still arriving for the cancelled utterance
      return;
    }
    // console.log('🔊 Received audio chunk, size:', blob.size);
    audioQueueRef.current.push(blob);
    
    if (!isPlayingRef.current) {
      playNextAudio();
    }
  }, [playNextAudio]);

  /**
   * Connect to WebSocket with ping/pong keepalive
   */
  const connect = useCallback(async () => {
    if (!user || !conversationId) {
      console.error('Cannot connect: missing user or conversationId');
      return;
    }

    try {
      // console.log('Connecting to voice WebSocket...');
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      const token = await user.getIdToken();
      const ws = new WebSocket(`${WS_URL}/voice?token=${encodeURIComponent(token)}`);
      
      ws.onopen = () => {
        // console.log('✅ WebSocket connected — waiting for ready');
        setError(null);

        // Start ping/pong keepalive (prevents idle timeout on public networks)
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 25000); // Every 25 seconds
        // Note: authenticate is sent when server sends 'ready', not here,
        // because the server registers its message listener asynchronously
        // after the connection event fires.
      };

      ws.onmessage = (event) => {
        if (event.data instanceof Blob) {
          handleAudioData(event.data);
          return;
        }

        try {
          const message = JSON.parse(event.data);
          handleServerMessage(message);
        } catch (err) {
          console.error('Failed to parse message:', err);
        }
      };

      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
        console.error('ReadyState at error:', ws.readyState);
        console.error('Buffered amount:', ws.bufferedAmount);
        
        let errorMessage = 'Connection error';
        
        // Context-aware error messages
        if (isRecording) {
          errorMessage = 'Network interrupted during recording. Check signal strength.';
        } else if (ws.bufferedAmount > 10000) {
          errorMessage = 'Network too slow for real-time audio. Try text mode.';
        } else if (browserInfo.isIOS) {
          errorMessage = 'Connection lost. Keep app in foreground on iOS.';
        } else if (browserInfo.isEdge) {
          errorMessage = 'Connection error. Check corporate network settings.';
        } else {
          errorMessage = 'Connection lost. Reconnecting...';
        }
        
        setError(errorMessage);
      };

      ws.onclose = (event) => {
        // console.log('WebSocket disconnected');
        // console.log('Close code:', event.code);
        // console.log('Close reason:', event.reason);
        // console.log('Was clean:', event.wasClean);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Determine what happened
        let errorMsg = null;
        if (event.code === 1006) {
          errorMsg = 'Connection interrupted - check network signal';
        } else if (event.code === 1001) {
          errorMsg = 'Connection closed - server going away';
        } else if (event.code === 1015) {
          errorMsg = 'TLS handshake failed';
        } else if (!event.wasClean) {
          errorMsg = 'Connection dropped unexpectedly';
        }
        
        if (errorMsg) {
          setError(errorMsg);
        }
        
        setIsConnected(false);
        recordingSessionActiveRef.current = false;
        
        // Auto-reconnect if conversation is still active
        if (conversationId) {
          reconnectTimeoutRef.current = setTimeout(() => {
            // console.log('Attempting to reconnect...');
            connect();
          }, 3000);
        }
      };

      wsRef.current = ws;

    } catch (err) {
      console.error('Failed to connect:', err);
      setError('Failed to connect: ' + err.message);
    }
  }, [user, conversationId, handleServerMessage, handleAudioData, isRecording, browserInfo]);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    
    if (wsRef.current) {
      // Null out handlers before closing to prevent spurious error events
      // and to stop the onclose handler from scheduling an auto-reconnect.
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    recordingSessionActiveRef.current = false;
  }, []);

  /**
   * Start audio level monitoring
   */
  const startAudioLevelMonitoring = useCallback(() => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateLevel = () => {
      if (!analyserRef.current) {
        setAudioLevel(0);
        return;
      }

      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const normalizedLevel = Math.min(100, (average / 128) * 100);

      setAudioLevel(normalizedLevel);
      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();
  }, []);

  /**
   * Start recording with resampling for cross-browser compatibility
   */
  const startRecording = useCallback(async () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('❌ WebSocket not connected');
      setError('Not connected to server');
      return;
    }

    // console.log('🎙️ Starting recording...');
    
    // Warn iOS users
    if (browserInfo.isIOS) {
      // console.log('📱 iOS detected - keep app in foreground while recording');
    }

    try {
      initializeAudioElement();

      const initialized = await initializeMicrophoneStream();
      
      if (!initialized) {
        console.error('❌ Failed to initialize microphone');
        return;
      }

      if (!audioStreamRef.current || !audioContextRef.current || !sourceNodeRef.current) {
        throw new Error('Audio infrastructure not ready');
      }

      // console.log('📤 Sending start_recording command...');
      wsRef.current.send(JSON.stringify({
        type: 'start_recording',
        conversationId: conversationId
      }));

      const nativeSampleRate = nativeSampleRateRef.current;
      const targetSampleRate = 16000;
      
      // console.log(`🔄 Will resample from ${nativeSampleRate}Hz to ${targetSampleRate}Hz`);

      const processor = audioContextRef.current.createScriptProcessor(2048, 1, 1);
      
      sourceNodeRef.current.connect(processor);
      processor.connect(audioContextRef.current.destination);

      processor.onaudioprocess = (e) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          return;
        }

        const inputData = e.inputBuffer.getChannelData(0);
        
        // Resample to 16kHz if needed (Firefox/Safari compatibility)
        let resampledData;
        if (nativeSampleRate !== targetSampleRate) {
          resampledData = resample(inputData, nativeSampleRate, targetSampleRate);
        } else {
          resampledData = inputData;
        }
        
        // Convert to Int16
        const int16Array = new Int16Array(resampledData.length);
        for (let i = 0; i < resampledData.length; i++) {
          const s = Math.max(-1, Math.min(1, resampledData[i]));
          int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        
        // Monitor send buffer (detect network congestion)
        try {
          if (wsRef.current.bufferedAmount > 50000) {
            console.warn('⚠️ WebSocket send buffer backing up:', wsRef.current.bufferedAmount);
          }
          wsRef.current.send(int16Array.buffer);
        } catch (err) {
          console.error('❌ Failed to send audio data:', err);
          stopRecording();
          setError('Network too slow for voice - try text mode');
        }
      };

      processorRef.current = processor;
      startAudioLevelMonitoring();

      // console.log('✅ Recording started');
      setIsRecording(true);
      setTranscript('');
      setError(null);

    } catch (err) {
      console.error('❌ Failed to start recording:', err);
      setError('Could not start recording: ' + err.message);
      setIsRecording(false);
      recordingSessionActiveRef.current = false;
    }
  }, [conversationId, initializeAudioElement, initializeMicrophoneStream, startAudioLevelMonitoring, browserInfo]);

  /**
   * Stop recording
   */
  const stopRecording = useCallback(() => {
    // console.log('⏹️ Stopping recording...');

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && recordingSessionActiveRef.current) {
      // console.log('📤 Sending stop_recording command...');
      wsRef.current.send(JSON.stringify({
        type: 'stop_recording'
      }));
      recordingSessionActiveRef.current = false;
    } else {
      // console.log('ℹ️ Skipping stop_recording (no active session)');
    }

    setIsRecording(false);
    setAudioLevel(0);

    // console.log('✅ Recording stopped (stream stays alive)');
  }, []);

  /**
   * Complete cleanup
   */
  const cleanupAudio = useCallback(() => {
    // console.log('🧹 Cleaning up audio resources...');

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
      audioStreamInitializedRef.current = false;
      // console.log('🛑 Audio stream stopped');
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (audioElementRef.current) {
      if (audioElementRef.current.src) {
        URL.revokeObjectURL(audioElementRef.current.src);
      }
      audioElementRef.current = null;
      audioInitializedRef.current = false;
    }

    recordingSessionActiveRef.current = false;
    // console.log('✅ Cleanup complete');
  }, []);

  /**
   * Generate speech
   */
  const generateSpeech = useCallback((text, voiceProfile = 'coach', options = {}) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('Not connected to server');
      return;
    }

    if (!text || !text.trim()) {
      console.warn('No text provided for speech generation');
      return;
    }

    // Ensure the audio element exists before speech blobs arrive (may not be
    // initialized if the user hasn't started voice recording yet).
    initializeAudioElement();

    // console.log('🔊 Requesting speech generation');
    speechActiveRef.current = true;
    setIsProcessing(true);
    setError(null);

    wsRef.current.send(JSON.stringify({
      type: 'generate_speech',
      text: text.trim(),
      voiceProfile,
      options
    }));
  }, [initializeAudioElement]);

  /**
   * Stop any in-progress or playing speech immediately (barge-in / manual stop).
   * Discards the queue and any chunks still in flight for the cancelled utterance.
   */
  const stopSpeaking = useCallback(() => {
    speechActiveRef.current = false;
    audioQueueRef.current = [];
    isPlayingRef.current = false;

    const audio = audioElementRef.current;
    if (audio) {
      audio.pause();
      if (audio.src) {
        URL.revokeObjectURL(audio.src);
        audio.removeAttribute('src');
      }
    }

    setIsSpeaking(false);
    setIsProcessing(false);
  }, []);

  /**
   * Toggle recording
   */
  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Effect: Handle connection lifecycle
   */
  useEffect(() => {
    return () => {
      // console.log('🧹 useVoiceChat effect cleanup');

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      cleanupAudio();
      disconnect();
    };
  }, [conversationId, user]);

  /**
   * Effect: Handle visibility changes (iOS/Safari)
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // console.log('⚠️ Page hidden - browser may suspend WebSocket');
        if (isRecording && browserInfo.isIOS) {
          setError('Keep app visible - iOS suspends background audio');
        }
      } else {
        // console.log('✅ Page visible again');
        if (!isConnected && conversationId) {
          // console.log('🔄 Reconnecting after visibility restore');
          connect();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRecording, isConnected, conversationId, browserInfo, connect]);

  /**
   * Effect: Handle online/offline (iOS network handoff)
   */
  useEffect(() => {
    const handleOnline = () => {
      // console.log('✅ Network restored');
      if (!isConnected && conversationId) {
        // console.log('🔄 Attempting reconnection after network restore');
        connect();
      }
    };
    
    const handleOffline = () => {
      // console.log('⚠️ Network lost');
      if (isRecording) {
        stopRecording();
        setError('Network connection lost');
      }
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isConnected, conversationId, isRecording, connect, stopRecording]);

  return {
    isConnected,
    isRecording,
    isProcessing,
    isSpeaking,
    transcript,
    error,
    audioLevel,
    browserInfo,
    startRecording,
    stopRecording,
    toggleRecording,
    generateSpeech,
    stopSpeaking,
    connect,
    disconnect,
    cleanupAudio,
    clearError
  };
};